/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { IconButton, Tooltip } from '@mui/material'
import {
	mono,
	boShared,
	boMotion,
} from '@components/systems/backoffice/backoffice.tokens'

import dayjs from 'dayjs'
import { PlaylistAdd } from '@mui/icons-material'
import { SeasonType } from '@schemas'

interface PropsType extends SeasonType {
	onClick?: () => void
	/** 판매 대기열에 추가. 이미 대기열에 있거나 판매중이면 버튼을 숨긴다. */
	onAddToQueue?: () => void
	isSelected: boolean
}

/**
 * 시즌 목록의 한 행 — 헤어라인 행. 선택은 surface 배경 + 좌측 1px ink 룰.
 * 상태는 색이 아니라 칩의 채움(판매중) / 외곽(활성) / 희미(종료·중지)로만 가른다.
 */
export const SeasonListItem = (props: PropsType) => {
	const dateRange = `${dayjs(props.startDate).format('YYYY.MM.DD')} - ${dayjs(
		props.endDate,
	).format('YYYY.MM.DD')}`

	const soldUsdPerTargetUsd = `${props.totalUsd.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	})} / ${props.targetUsd.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	})}`
	const soldQuantityPerTargetQuantity = `${props.soldQuantity.toLocaleString()} / ${props.targetQuantity.toLocaleString()}`
	// openYn 이 status 보다 우선한다. 지금 shop 에 노출되는 시즌은 전체에서 하나뿐이고,
	// 그것을 한눈에 구분하는 것이 이 목록의 핵심이다. (SEASON_OPEN_PLAN.md §4)
	const isOpen = props.openYn === 1
	const statusTitle = isOpen
		? '판매중'
		: props.status === 'not_start'
		? '시작 전'
		: props.status === 'expired'
		? '기간 종료'
		: props.status === 'inactive'
		? '사용 중지'
		: props.status === 'sold_out'
		? '완판'
		: '활성화'
	const statusClassName = isOpen ? 'open' : props.status.toLowerCase()
	const chipTone: string = isOpen
		? 'solid'
		: props.status === 'active' || props.status === 'not_start'
		? 'outline'
		: props.status === 'expired' || props.status === 'inactive'
		? 'faint'
		: ''

	return (
		<View css={style}>
			{/* 행 안에 대기열 버튼이 있어 <button> 중첩이 된다 — 행은 div + role=button */}
			<View
				className={`seasonRow ${props.isSelected ? 'selected' : statusClassName}`}
				role="button"
				tabIndex={0}
				onClick={props.onClick}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						props.onClick?.()
					}
				}}
			>
				<View className="rowMain">
					<View className="header">
						<span className="title">{props.title}</span>
						<span className={`boChip ${chipTone}`}>{statusTitle}</span>
					</View>

					<View className="facts">
						<View className="fact">
							<span className="factLabel">판매기간</span>
							<span className="factValue">{dateRange}</span>
						</View>
						<View className="fact">
							<span className="factLabel">판매금액</span>
							<span className="factValue">{soldUsdPerTargetUsd}</span>
						</View>
						<View className="fact">
							<span className="factLabel">판매수량</span>
							<span className="factValue">{soldQuantityPerTargetQuantity}</span>
						</View>
					</View>
				</View>

				<View className="buttonContainer">
					{!isOpen && props.queueOrder === null && (
						<Tooltip title="판매 대기열에 추가">
							<IconButton
								size="small"
								onClick={e => {
									e.stopPropagation()
									props.onAddToQueue?.()
								}}
							>
								<PlaylistAdd fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</View>
			</View>
		</View>
	)
}

const style = css({
	...boShared,
	position: 'relative',
	display: 'flex',

	'.seasonRow': {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 12,
		width: '100%',
		height: 'fit-content',
		padding: '14px 12px 14px 16px',
		textAlign: 'left',
		borderRadius: 0,
		borderBottom: `1px solid ${mono.line}`,
		borderLeft: '1px solid transparent',
		background: 'transparent',
		transition: boMotion,

		'&:hover': { background: mono.surface },

		'&.selected': {
			background: mono.surface,
			borderLeftColor: mono.ink,

			'.title': { fontWeight: 500 },
		},

		'.rowMain': {
			flex: 1,
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 10,
		},

		'.header': {
			display: 'flex',
			alignItems: 'center',
			gap: 10,
			minHeight: 22,

			'.title': {
				fontSize: 15,
				fontWeight: 400,
				color: mono.ink,
				lineHeight: 1.3,
			},
		},

		'.facts': {
			display: 'flex',
			flexDirection: 'column',
			gap: 4,

			'.fact': {
				display: 'flex',
				alignItems: 'baseline',
				gap: 10,
			},
			'.factLabel': {
				flexShrink: 0,
				width: 56,
				fontSize: 11,
				letterSpacing: '.12em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
			'.factValue': {
				fontSize: 12.5,
				fontWeight: 300,
				color: mono.dim,
				fontVariantNumeric: 'tabular-nums',
			},
		},

		'.buttonContainer': {
			flexShrink: 0,
			display: 'flex',
			gap: 4,
			marginTop: -4,
		},
	},
})
